import type * as React from 'react'
import {
  useCallback,
  useState,
  useEffect,
  createContext,
  useContext,
} from 'react'
import { Temporal } from '@js-temporal/polyfill'
import { clsx } from 'clsx'

import { getDefaultTimeZone } from '../../helpers/timeZone'
import { Calendar } from '../Calendar'
import type { CalendarProps } from '../Calendar'
import { TimeInput, formatTimeZone } from '../TimeInput'
import type { TimeInputProps } from '../TimeInput'
import { Separator } from '../Separator'
import type { SeparatorProps } from '../Separator'
import { ClockIcon } from '../icons'
import { Text } from '../Text'
import { Flex } from '../Flex'

import styles from './DateTimePicker.module.css'

function zonedDateTimeToPlainDate(
  zonedDateTime?: Temporal.ZonedDateTime,
): Temporal.PlainDate | undefined {
  if (!zonedDateTime) return undefined
  return zonedDateTime.toPlainDate()
}

function zonedDateTimeToPlainTime(
  zonedDateTime?: Temporal.ZonedDateTime,
): Temporal.PlainTime | undefined {
  if (!zonedDateTime) return undefined
  return zonedDateTime.toPlainTime()
}

function createZonedDateTime(
  date: Temporal.PlainDate,
  time: Temporal.PlainTime,
  timezone: Temporal.TimeZoneLike,
): Temporal.ZonedDateTime {
  // Combine the date and time into a PlainDateTime
  const dateTime = date.toPlainDateTime(time)
  // Convert to ZonedDateTime using the specified timezone
  return dateTime.toZonedDateTime(timezone)
}

/**
 * Context value shared among DateTimePicker sub-components.
 *
 * This context provides access to the DateTimePicker's state and configuration,
 * allowing sub-components (Calendar, TimeInput, Separator) to interact with the
 * parent component without prop drilling.
 *
 * @example
 * import { useDateTimePickerContext } from '@buffer-mono/popcorn'
 *
 * const CustomTimeButton = () => {
 *   const { setTime, timezone } = useDateTimePickerContext()
 *
 *   const handleQuickTime = () => {
 *     setTime(Temporal.PlainTime.from('09:00'))
 *   }
 *
 *   return <button onClick={handleQuickTime}>Set to 9 AM</button>
 * }
 */
export interface DateTimePickerContextValue {
  value?: Temporal.ZonedDateTime
  setValue: (value: Temporal.ZonedDateTime | undefined) => void
  /**
   * Update only the date portion while preserving the time.
   * Triggers both onValueChange and onDateChange callbacks.
   */
  setDate: (date: Temporal.PlainDate) => void
  /**
   * Update only the time portion while preserving the date.
   * Triggers both onValueChange and onTimeChange callbacks.
   */
  setTime: (time: Temporal.PlainTime) => void
  min?: Temporal.ZonedDateTime
  max?: Temporal.ZonedDateTime
  minTime?: Temporal.PlainTime
  maxTime?: Temporal.PlainTime
  timezone: string
  hourCycle: 12 | 24
  weekStartsOn: 0 | 1
  required: boolean
}

/**
 * This context is used internally to share state between the root DateTimePicker
 * component and its sub-components (Calendar, TimeInput, Separator).
 */
const DateTimePickerContext = createContext<
  DateTimePickerContextValue | undefined
>(undefined)

/**
 * Hook to access DateTimePicker context from sub-components or custom components.
 *
 * This hook provides access to the DateTimePicker's state and configuration,
 * enabling custom components to interact with the picker's functionality.
 *
 * @throws {Error} If used outside of a DateTimePicker component
 * @returns The DateTimePicker context value
 */
export function useDateTimePickerContext(): DateTimePickerContextValue {
  const context = useContext(DateTimePickerContext)
  if (!context) {
    throw new Error(
      'useDateTimePickerContext must be used within a DateTimePicker component',
    )
  }
  return context
}

/**
 * Props for the DateTimePicker component.
 *
 * The DateTimePicker can be used in controlled or uncontrolled mode:
 * - **Controlled**: Provide both `value` and `onValueChange`
 * - **Uncontrolled**: Provide only `defaultValue`
 */
export interface DateTimePickerProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'defaultValue' | 'value' | 'onChange'
> {
  /**
   * The current value (controlled mode).
   *
   * When provided, the component operates in controlled mode.
   * You must also provide `onValueChange` to handle updates.
   *
   * @example
   * const [value, setValue] = useState<Temporal.ZonedDateTime>()
   * <DateTimePicker value={value} onValueChange={setValue} />
   */
  value?: Temporal.ZonedDateTime
  /**
   * The default value (uncontrolled mode).
   *
   * Sets the initial value when the component is not controlled.
   * The component manages its own state internally.
   *
   * @example
   * <DateTimePicker defaultValue={Temporal.Now.zonedDateTimeISO(getBrowserTimeZone())} />
   */
  defaultValue?: Temporal.ZonedDateTime
  /**
   * Handler called when the complete date-time value changes.
   *
   * This fires for both date and time changes. Use this for general
   * value updates. For more granular control, use `onDateChange` and
   * `onTimeChange` to detect specific user interactions.
   *
   * @param value - The new ZonedDateTime value (undefined if cleared)
   *
   * @example
   * // Track all changes
   * <DateTimePicker
   *   onValueChange={(value) => {
   *     console.log('New value:', value?.toString())
   *   }}
   * />
   */
  onValueChange?: (value: Temporal.ZonedDateTime | undefined) => void
  /**
   * Handler called when the user selects a date from the calendar.
   *
   * This fires independently from `onValueChange` and is useful for
   * detecting user interaction specifically with the calendar component.
   * Both callbacks will be triggered on date selection.
   *
   * @param date - The selected PlainDate
   * @param value - The complete ZonedDateTime with the updated date
   *
   * @example
   * // Detect calendar interaction
   * <DateTimePicker
   *   onDateChange={(date, value) => {
   *     console.log('User selected date:', date.toString())
   *     trackEvent('calendar_date_selected')
   *   }}
   * />
   */
  onDateChange?: (
    date: Temporal.PlainDate,
    value: Temporal.ZonedDateTime | undefined,
  ) => void
  /**
   * Handler called when the user changes the time input.
   *
   * This fires independently from `onValueChange` and is useful for
   * detecting user interaction specifically with the time input.
   * Both callbacks will be triggered on time changes.
   *
   * @param time - The selected PlainTime
   * @param value - The complete ZonedDateTime with the updated time
   *
   * @example
   * // Detect time input interaction
   * <DateTimePicker
   *   onTimeChange={(time, value) => {
   *     console.log('User changed time:', time.toString())
   *     trackEvent('time_input_changed')
   *   }}
   * />
   */
  onTimeChange?: (
    time: Temporal.PlainTime,
    value: Temporal.ZonedDateTime | undefined,
  ) => void
  /**
   * The timezone for the date-time picker.
   *
   * @default The browser's time zone, or a zone matching its UTC offset when the browser cannot resolve one
   */
  timezone?: string
  /**
   * The minimum allowed date and time.
   *
   * @example
   * <DateTimePicker min={Temporal.Now.zonedDateTimeISO(getBrowserTimeZone())} />
   */
  min?: Temporal.ZonedDateTime
  /**
   * The maximum allowed date and time.
   *
   * @example
   * <DateTimePicker
   *   max={Temporal.Now.zonedDateTimeISO(getBrowserTimeZone()).add({ days: 30 })}
   * />
   */
  max?: Temporal.ZonedDateTime
  /**
   * The minimum allowed time across all dates.
   *
   * @example
   * <DateTimePicker minTime={Temporal.PlainTime.from('09:00')} />
   */
  minTime?: Temporal.PlainTime
  /**
   * The maximum allowed time across all dates.
   *
   * @example
   * <DateTimePicker maxTime={Temporal.PlainTime.from('17:00')} />
   */
  maxTime?: Temporal.PlainTime
  /**
   * The hour cycle format for time display.
   *
   * @default 12
   *
   * @example
   * <DateTimePicker hourCycle={24} /> // 24-hour format (e.g., 14:30)
   */
  hourCycle?: 12 | 24
  /**
   * The first day of the week in the calendar.
   *
   * @default 1 (Monday)
   *
   * @example
   * <DateTimePicker weekStartsOn={0} /> // Week starts on Sunday
   */
  weekStartsOn?: 0 | 1
  className?: string
  style?: React.CSSProperties
  required?: boolean
  children?: React.ReactNode
}

/**
 * DateTimePicker - A compound component for selecting date and time values.
 *
 * This component uses the Temporal API to provide timezone-aware date-time selection.
 * It returns a `Temporal.ZonedDateTime` object that includes full timezone information.
 *
 * Requires explicit composition of sub-components (Calendar, TimeInput, Separator).
 *
 * @example
 * import { DateTimePicker } from '@buffer-mono/popcorn'
 * import { Temporal } from '@js-temporal/polyfill'
 *
 * function SchedulePost() {
 *   const [scheduledTime, setScheduledTime] = useState<Temporal.ZonedDateTime>()
 *
 *   return (
 *     <DateTimePicker
 *       value={scheduledTime}
 *       onValueChange={setScheduledTime}
 *       min={Temporal.Now.zonedDateTimeISO(getBrowserTimeZone())}
 *       timezone="America/New_York"
 *     >
 *       <DateTimePicker.Calendar />
 *       <DateTimePicker.Separator />
 *       <DateTimePicker.TimeInput />
 *     </DateTimePicker>
 *   )
 * }
 *
 * @example
 * // Custom composition with additional elements
 * <DateTimePicker value={value} onValueChange={setValue}>
 *   <DateTimePicker.Calendar />
 *   <DateTimePicker.Separator />
 *   <CustomTimeSuggestions />
 *   <DateTimePicker.TimeInput />
 * </DateTimePicker>
 *
 * @see {@link DateTimePickerContextValue} for context API details
 * @see {@link useDateTimePickerContext} for accessing context in custom components
 */
const DateTimePickerRoot: React.FC<DateTimePickerProps> = ({
  value,
  defaultValue,
  onValueChange,
  timezone = getDefaultTimeZone(),
  min,
  max,
  minTime,
  maxTime,
  hourCycle = 12,
  weekStartsOn = 1,
  className,
  onTimeChange,
  onDateChange,
  required = false,
  children,
  ...rest
}) => {
  const [internalValue, setInternalValue] = useState<
    Temporal.ZonedDateTime | undefined
  >(value !== undefined ? value : defaultValue)

  useEffect(
    function updateInternalValue() {
      if (value !== undefined) {
        setInternalValue(value)
      }
    },
    [value],
  )

  const effectiveValue = value !== undefined ? value : internalValue

  const handleSetValue = useCallback(
    (newValue: Temporal.ZonedDateTime | undefined) => {
      if (onValueChange) {
        onValueChange(newValue)
      } else {
        setInternalValue(newValue)
      }
    },
    [onValueChange],
  )

  const handleSetDate = useCallback(
    (date: Temporal.PlainDate) => {
      const currentTime =
        zonedDateTimeToPlainTime(effectiveValue) || new Temporal.PlainTime()
      const newValue = createZonedDateTime(date, currentTime, timezone)

      // Trigger granular callback
      onDateChange?.(date, newValue)

      // Trigger main value change
      handleSetValue(newValue)
    },
    [effectiveValue, timezone, onDateChange, handleSetValue],
  )

  const handleSetTime = useCallback(
    (time: Temporal.PlainTime) => {
      const currentDate = zonedDateTimeToPlainDate(effectiveValue)
      if (!currentDate) {
        // Can't set time without a date
        return
      }

      const newValue = createZonedDateTime(currentDate, time, timezone)

      // Trigger granular callback
      onTimeChange?.(time, newValue)

      // Trigger main value change
      handleSetValue(newValue)
    },
    [effectiveValue, timezone, onTimeChange, handleSetValue],
  )

  const contextValue: DateTimePickerContextValue = {
    value: effectiveValue,
    setValue: handleSetValue,
    setDate: handleSetDate,
    setTime: handleSetTime,
    min,
    max,
    minTime,
    maxTime,
    timezone,
    hourCycle,
    weekStartsOn,
    required,
  }

  return (
    <DateTimePickerContext.Provider value={contextValue}>
      <div className={clsx(styles.root, className)} {...rest}>
        {children}
      </div>
    </DateTimePickerContext.Provider>
  )
}

export type DateTimePickerCalendarProps = Pick<CalendarProps, 'startMonth'>

export type DateTimePickerTimeInputProps = Pick<
  TimeInputProps,
  | 'type'
  | 'className'
  | 'hourCycle'
  | 'id'
  | 'timeOverrides'
  | 'disabled'
  | 'readOnly'
  | 'required'
  | 'granularity'
  | 'placeholder'
  | 'size'
>

export type DateTimePickerSeparatorProps = SeparatorProps

/**
 * Calendar sub-component for DateTimePicker.
 *
 * Renders a calendar for date selection. This component automatically:
 * - Accesses the DateTimePicker context for shared state
 * - Syncs with the current date value
 * - Respects min/max date constraints
 * - Triggers both onValueChange and onDateChange callbacks on selection
 *
 * Must be used within a DateTimePicker component.
 */
const DateTimePickerCalendar: React.FC<DateTimePickerCalendarProps> = ({
  startMonth,
}) => {
  const { value, setDate, min, max, timezone, weekStartsOn, required } =
    useDateTimePickerContext()

  const dateValue = zonedDateTimeToPlainDate(value)
  const minDate = min ? min.toPlainDate() : undefined
  const maxDate = max ? max.toPlainDate() : undefined

  const handleDateChange = useCallback(
    (date?: Temporal.PlainDate) => {
      if (date) {
        setDate(date)
      }
    },
    [setDate],
  )

  return (
    <Calendar
      value={dateValue}
      onValueChange={handleDateChange}
      timezone={timezone}
      weekStartsOn={weekStartsOn}
      min={minDate}
      max={maxDate}
      required={required}
      startMonth={startMonth}
    />
  )
}

DateTimePickerCalendar.displayName = 'DateTimePicker.Calendar'

/**
 * TimeInput sub-component for DateTimePicker.
 *
 * Renders a time input field. This component automatically:
 * - Accesses the DateTimePicker context for shared state
 * - Syncs with the current time value
 * - Becomes disabled when no date is selected
 * - Respects min/max time constraints (including dynamic constraints from min/max date)
 * - Triggers both onValueChange and onTimeChange callbacks on changes
 * - Supports overriding hourCycle per instance
 *
 * Must be used within a DateTimePicker component.
 *
 * ## Dynamic Time Constraints
 *
 * When the selected date matches the min or max date boundary, the time portion
 * of min/max automatically becomes the active time constraint for that day.
 */
const DateTimePickerTimeInput: React.FC<DateTimePickerTimeInputProps> = ({
  className,
  hourCycle: propHourCycle,
  id,
  timeOverrides,
  disabled,
  readOnly,
  required,
  granularity,
  placeholder,
  size,
  ...props
}) => {
  const {
    value,
    setTime,
    min,
    max,
    minTime,
    maxTime,
    timezone,
    hourCycle: contextHourCycle,
  } = useDateTimePickerContext()

  const dateValue = zonedDateTimeToPlainDate(value)
  const timeValue = zonedDateTimeToPlainTime(value)

  // Allow prop to override context hourCycle
  const effectiveHourCycle = propHourCycle ?? contextHourCycle

  // Calculate effective min/max time based on selected date
  let effectiveMinTime: Temporal.PlainTime | undefined = minTime

  if (!effectiveMinTime && min && dateValue) {
    const overallMinDate = min.toPlainDate()
    if (dateValue.equals(overallMinDate)) {
      effectiveMinTime = min.toPlainTime()
    }
  }

  let effectiveMaxTime: Temporal.PlainTime | undefined = maxTime

  if (!effectiveMaxTime && max && dateValue) {
    const overallMaxDate = max.toPlainDate()
    if (dateValue.equals(overallMaxDate)) {
      effectiveMaxTime = max.toPlainTime()
    }
  }

  const handleTimeChange = useCallback(
    (time: Temporal.PlainTime | null) => {
      if (time) {
        setTime(time)
      }
    },
    [setTime],
  )

  const prefix = <ClockIcon size="small" strokeWidth={2} />

  const suffix = timezone ? (
    <Flex justify="end">
      <Text size="sm">{formatTimeZone(timezone)}</Text>
    </Flex>
  ) : null

  return (
    <TimeInput
      {...props}
      value={timeValue}
      onChange={handleTimeChange}
      disabled={disabled ?? !dateValue}
      readOnly={readOnly}
      required={required}
      min={effectiveMinTime}
      max={effectiveMaxTime}
      hourCycle={effectiveHourCycle}
      granularity={granularity}
      placeholder={placeholder}
      prefix={prefix}
      suffix={suffix}
      className={className}
      size={size}
      id={id}
      timeOverrides={timeOverrides}
    />
  )
}

DateTimePickerTimeInput.displayName = 'DateTimePicker.TimeInput'

/**
 * Separator sub-component for DateTimePicker.
 *
 * Renders a visual separator between DateTimePicker sub-components.
 * Typically used to divide the Calendar and TimeInput components.
 *
 * This is a presentational component that doesn't interact with the DateTimePicker context.
 *
 * @example
 * // Standard usage
 * <DateTimePicker value={value} onValueChange={setValue}>
 *   <DateTimePicker.Calendar />
 *   <DateTimePicker.Separator />
 *   <DateTimePicker.TimeInput />
 * </DateTimePicker>
 *
 * @example
 * // With custom styling
 * <DateTimePicker value={value} onValueChange={setValue}>
 *   <DateTimePicker.Calendar />
 *   <DateTimePicker.Separator className="my-separator" />
 *   <DateTimePicker.TimeInput />
 * </DateTimePicker>
 */
const DateTimePickerSeparator: React.FC<DateTimePickerSeparatorProps> = ({
  className,
  ...props
}) => {
  return <Separator className={clsx(styles.separator, className)} {...props} />
}

DateTimePickerSeparator.displayName = 'DateTimePicker.Separator'

/**
 * Compound component assembly using Object.assign pattern.
 *
 * This exports the DateTimePicker as a compound component with sub-components
 * attached as static properties, enabling the dot notation syntax:
 * - DateTimePicker.Calendar
 * - DateTimePicker.TimeInput
 * - DateTimePicker.Separator
 */
export const DateTimePicker = Object.assign(DateTimePickerRoot, {
  Calendar: DateTimePickerCalendar,
  TimeInput: DateTimePickerTimeInput,
  Separator: DateTimePickerSeparator,
})
